import { useOtChainState } from '@react/redux/store'

import { PoolAccountCTO, PoolFlexAccountCTO, PoolFlexOverviewCTO, PoolOverviewCTO } from '@shared/sharedTypes'
import React, { useEffect, useState } from 'react'

import { Card, CardContent, Typography } from '@mui/material'
import { erc20WeiToCommaDecString } from '@shared/conversions'
import { logOT } from '@shared/loggingShared'

interface TotalPrincipalAllPoolsProps {
  lenderAddr: string
  currency: string
  callback: (newTotal: number) => void
}

export const TotalPrincipalAllPools: React.FC<TotalPrincipalAllPoolsProps> = ({ lenderAddr, currency, callback }) => {
  const {
    stageConfiguration: { graphqlUrl, restAPIKey },
    chainConfiguration: { chainName, providerReadOnly },
  } = useOtChainState()

  const [totalFlex, setTotalFlex] = useState<number>(0)
  const [totalTerm, setTotalTerm] = useState<number>(0)
  const [totalForAccount, setTotalForAccount] = useState<number>(0)
  const isLocal: boolean = chainName === 'LocalHardhat'
  const [isLoading, setIsLoading] = useState<boolean>(true)

  useEffect(() => {
    async function fetchTotalPrincipalAllPools(): Promise<void> {
      if (!providerReadOnly) return
      setIsLoading(true)
      const startDate = isLocal ? new Date('2024-11-03') : new Date()
      const endDate = isLocal ? new Date('2024-11-03') : new Date()
      const allFlexPools = await fetchFlexOverviews(startDate, endDate)
      const filteredPools = allFlexPools.filter((pool) => {
        return pool.liquidityTokenSymbol === currency
      })
      let totalFlex = 0
      filteredPools.forEach((vault) => {
        totalFlex += Number(vault.totalPrincipalEarningInterest)
      })
      setTotalFlex(totalFlex)

      const allTermPools = await fetchTermOverviews(startDate, endDate)
      const filteredTermPools = allTermPools.filter((pool) => pool.liquidityTokenSymbol === currency)
      let totalTerm = 0
      filteredTermPools.forEach((vault) => {
        totalTerm += Number(vault.totalOutstandingLoanPrincipal)
      })
      setTotalTerm(totalTerm)

      setTotalForAccount(0)

      if (lenderAddr && lenderAddr !== 'All Lenders') {
        let totalForAccount = 0
        const flexPromises = filteredPools.map((pool) =>
          fetchFlexAccount(pool.poolAddr, lenderAddr).then((account) => {
            if (account) {
              totalForAccount += Number(account.principalEarningInterest)
            }
          })
        )

        const termPromises = filteredTermPools.map((pool) =>
          fetchTermAccount(pool.poolAddr, lenderAddr).then((account) => {
            if (account) {
              totalForAccount += Number(account.principalEarningInterest)
            }
          })
        )
        await Promise.all([...flexPromises, ...termPromises])
        setTotalForAccount(totalForAccount)
        callback(totalForAccount)
      } else {
        callback(totalFlex + totalTerm)
      }
      setIsLoading(false)
    }
    void fetchTotalPrincipalAllPools()
  }, [restAPIKey, providerReadOnly, lenderAddr, currency])

  const fetchFlexOverviews = async (startDate: Date, endDate: Date): Promise<PoolFlexOverviewCTO[]> => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires, @typescript-eslint/no-require-imports
      const axios = require('axios')
      const propertyListString = [
        'id',
        'poolType',
        'poolAddr',
        'totalPrincipalEarningInterest',
        'liquidityAssetAddr',
      ].join('\n')
      const query = `query GetPoolFlexOverviewsForDateRange($startDayStr: String!, $endDayStr: String!) {
        getPoolFlexOverviewsForDateRange(
            startDayStr: $startDayStr
            endDayStr: $endDayStr
        ) {
            ${propertyListString}
        }
      }`
      const startDayStr = `${startDate.getFullYear()}-${startDate.getMonth() + 1}-${startDate.getDate()}`
      const endDayStr = `${endDate.getFullYear()}-${endDate.getMonth() + 1}-${endDate.getDate()}`

      const variables = {
        startDayStr,
        endDayStr,
      }

      const response = await axios.post(
        `${graphqlUrl}/graphql`,
        {
          query: query,
          variables: variables,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': restAPIKey,
          },
        }
      )

      return response.data.data.getPoolFlexOverviewsForDateRange
    } catch (e) {
      logOT.error('fetchPoolOverviewCTOs error', e)
      return []
    }
  }

  const fetchTermOverviews = async (startDate: Date, endDate: Date): Promise<PoolOverviewCTO[]> => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires, @typescript-eslint/no-require-imports
      const axios = require('axios')
      const propertyListString = [
        'id',
        'poolType',
        'poolAddr',
        'totalOutstandingLoanPrincipal',
        'liquidityAssetAddr',
      ].join('\n')
      const query = `query GetPoolTermOverviewsForDateRange($startDayStr: String!, $endDayStr: String!) {
        getPoolTermOverviewsForDateRange(
            startDayStr: $startDayStr
            endDayStr: $endDayStr
        ) {
            ${propertyListString}
        }
      }`
      const startDayStr = `${startDate.getFullYear()}-${startDate.getMonth() + 1}-${startDate.getDate()}`
      const endDayStr = `${endDate.getFullYear()}-${endDate.getMonth() + 1}-${endDate.getDate()}`

      const variables = {
        startDayStr,
        endDayStr,
      }

      const response = await axios.post(
        `${graphqlUrl}/graphql`,
        {
          query: query,
          variables: variables,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': restAPIKey,
          },
        }
      )

      return response.data.data.getPoolTermOverviewsForDateRange
    } catch (e) {
      logOT.error('fetchPoolOverviewCTOs error', e)
      return []
    }
  }

  const fetchFlexAccount = async (poolAddr: string, accountAddr: string): Promise<PoolFlexAccountCTO | undefined> => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires, @typescript-eslint/no-require-imports
      const axios = require('axios')
      const propertyListString = ['id', 'poolAddr', 'principalEarningInterest'].join('\n')
      const query = `query GetPoolFlexAccount($poolAddr: String!, $accountAddr: String!) {
        getPoolFlexAccount(
            poolAddr: $poolAddr
            accountAddr: $accountAddr
        ) {
            ${propertyListString}
        }
      }`

      const variables = {
        poolAddr,
        accountAddr,
      }

      const response = await axios.post(
        `${graphqlUrl}/graphql`,
        {
          query: query,
          variables: variables,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': restAPIKey,
          },
        }
      )

      return response.data.data.getPoolFlexAccount
    } catch (e) {
      logOT.error('fetchPoolOverviewCTOs error', e)
    }
  }

  const fetchTermAccount = async (poolAddr: string, accountAddr: string): Promise<PoolAccountCTO | undefined> => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires, @typescript-eslint/no-require-imports
      const axios = require('axios')
      const propertyListString = ['id', 'poolAddr', 'principalEarningInterest'].join('\n')
      const query = `query GetPoolTermAccount($poolAddr: String!, $accountAddr: String!) {
        getPoolTermAccount(
            poolAddr: $poolAddr
            accountAddr: $accountAddr
        ) {
            ${propertyListString}
        }
      }`

      const variables = {
        poolAddr,
        accountAddr,
      }

      const response = await axios.post(
        `${graphqlUrl}/graphql`,
        {
          query: query,
          variables: variables,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': restAPIKey,
          },
        }
      )

      return response.data.data.getPoolTermAccount
    } catch (e) {
      logOT.error('fetchPoolOverviewCTOs error', e)
    }
  }

  return (
    <Card>
      <CardContent>
        <Typography sx={{ color: '#666' }} variant="h6">
          Current Outstanding Loan Principal - <span style={{ fontWeight: 'bold', color: '#333' }}>All Vaults</span>
        </Typography>
        {isLoading && (
          <Typography sx={{ fontWeight: 'normal', fontSize: '1em' }} variant="body2">
            Loading...
          </Typography>
        )}
        {!isLoading && (
          <Typography sx={{ fontWeight: 'bold', fontSize: '1.5em' }} variant="body2">
            {erc20WeiToCommaDecString(lenderAddr === 'All Lenders' ? totalFlex + totalTerm : totalForAccount)}{' '}
            {currency}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}
